import { z } from "zod";

// Card payments through Stripe Connect. Clevis's own Stripe account is the
// platform (it also bills each company for Clevis); every company that takes
// cards has its own connected Stripe account, verified by Stripe, paid out to
// its own bank. Customer payments are charged on the company's account, so
// fees, refunds and disputes are theirs. The super admin turns the
// "stripe-payments" integration on per company; its admin then connects.
// Written by functions only (settings/payments).

export const CARD_PAYMENTS_SLUG = "stripe-payments";

export const CardPaymentsStatusSchema = z.enum([
  "not_started", // nothing at Stripe yet
  "onboarding", // account made, Stripe's setup not finished
  "pending", // submitted, Stripe is reviewing
  "active", // can take cards
  "needs_info", // can take cards, but Stripe wants more soon
  "restricted", // Stripe has paused charges or payouts until something is fixed
]);
export type CardPaymentsStatus = z.infer<typeof CardPaymentsStatusSchema>;

export const CardPaymentsSchema = z.object({
  stripeAccountId: z.string().nullable().default(null),
  status: CardPaymentsStatusSchema.default("not_started"),
  chargesEnabled: z.boolean().default(false),
  payoutsEnabled: z.boolean().default(false),
  detailsSubmitted: z.boolean().default(false),
  /** What Stripe still needs, in plain words. */
  requirementsDue: z.array(z.string()).default([]),
  /** When Stripe needs it by (ms), if it gave a deadline. */
  requirementsDeadline: z.number().nullable().default(null),
  disabledReason: z.string().default(""),
  /** The business name customers see on the Stripe page and their statement. */
  businessName: z.string().default(""),
  livemode: z.boolean().default(false),
  connectedAt: z.number().nullable().default(null),
  checkedAt: z.number().nullable().default(null),
  updatedAt: z.number().default(0),
});
export type CardPayments = z.infer<typeof CardPaymentsSchema>;

/** Card buttons work only when Stripe says charges are on. */
export const cardPaymentsReady = (p: Partial<CardPayments> | null | undefined): boolean =>
  !!p?.stripeAccountId && p.chargesEnabled === true && (p.status === "active" || p.status === "needs_info");
